import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Accessibility, MessageCircle } from "lucide-react";

import ScrollProgress from "./components/ScrollProgress";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Features from "./components/Features";
import About from "./components/About";
import Services from "./components/Services";
import Gallery from "./components/Gallery";
import Reviews from "./components/Reviews";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import AccessibilityPanel from "./components/AccessibilityPanel";

import { contactDetails, type Lang, type Theme } from "./content";

export default function App() {
  const [lang, setLang] = useState<Lang>("he");
  const [theme, setTheme] = useState<Theme>("dark");
  const [a11yOpen, setA11yOpen] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme") as Theme | null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute("data-theme", saved);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "he" ? "rtl" : "ltr";
  }, [lang]);

  const toggleLang = () => setLang((l) => (l === "he" ? "en" : "he"));

  const toggleTheme = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("theme", next);
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary selection:text-black">
      <ScrollProgress />
      <Header lang={lang} toggleLang={toggleLang} theme={theme} toggleTheme={toggleTheme} />

      {/* Floating action buttons */}
      <div
        className={`fixed bottom-6 ${lang === "he" ? "left-6" : "right-6"} z-40 flex flex-col gap-4`}
      >
        <button
          onClick={() => setA11yOpen((v) => !v)}
          className="bg-blue-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
          title="Accessibility"
        >
          <Accessibility className="w-6 h-6" />
        </button>
        <a
          href={contactDetails.whatsapp.href}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
          aria-label="WhatsApp"
        >
          <MessageCircle className="w-6 h-6 fill-current" />
        </a>
      </div>

      <AnimatePresence>
        {a11yOpen && <AccessibilityPanel onClose={() => setA11yOpen(false)} lang={lang} />}
      </AnimatePresence>

      <CookieConsent lang={lang} />

      <Hero lang={lang} />
      <Features lang={lang} />
      <About lang={lang} />
      <Services lang={lang} />
      <Gallery lang={lang} />
      <Reviews lang={lang} />
      <Contact lang={lang} />
      <Footer lang={lang} />
    </div>
  );
}
